import {
  FC, useEffect, useState, useCallback, memo,
} from 'react';
import dayjs from 'dayjs';

import {
  CardGrid,
  AddCard,
} from 'shared/ui';
import { EngineRequestDto, OrderRequestDto } from 'shared/api';
import { EngineListItem } from 'entities/engine';
import { useAppSelector } from 'app/providers/Store';
import { IStore } from 'app/providers/Store/model/store';
import { showConfirm } from 'shared/utils';
import { useOrders } from 'pages/Orders/hooks/useOrders';
import { OrderForm } from 'pages/Orders/components/OrderForm';
import { useEngines } from '../hooks/useEngines';
import {
  EngineCard,
  EngineForm,
  EngineDetailModal,
} from '../components';
import styles from './EnginesPage.module.scss';

type EngineFormData = Omit<EngineRequestDto, 'lastMaintenance' | 'nextMaintenance' | 'plannedDateOfStop'> & {
  lastMaintenance?: dayjs.Dayjs;
  nextMaintenance?: dayjs.Dayjs;
  plannedDateOfStop?: dayjs.Dayjs;
};

export const EnginesPage: FC = memo(() => {
  const {
    engines,
    loadEngines,
    createNewEngine,
    updateExistingEngine,
    deleteEngineById,
    loadEngineDetail,
    getStatusInfo,
    formatDate,
  } = useEngines();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedEngineId, setSelectedEngineId] = useState<number | null>(null);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);
  const [orderEngineId, setOrderEngineId] = useState<number | null>(null);
  const { user } = useAppSelector((state: IStore) => state.userSlice);
  const { createNewOrder } = useOrders();

  useEffect(() => {
    loadEngines();
  }, []);

  const handleCreate = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (id: number) => {
    const confirmed = await showConfirm({
      title: 'Подтверждение удаления',
      content: 'Вы уверены, что хотите удалить этот двигатель? Это действие нельзя отменить.',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okType: 'danger',
    });

    if (confirmed) {
      await deleteEngineById(id);
    }
  }, [deleteEngineById]);

  const handleSubmit = useCallback(async (data: EngineFormData) => {
    // Преобразуем объекты dayjs обратно в строки для API
    const processedData: EngineRequestDto = {
      ...data,
      lastMaintenance: data.lastMaintenance ? data.lastMaintenance.format('YYYY-MM-DDTHH:mm:ss') : undefined,
      nextMaintenance: data.nextMaintenance ? data.nextMaintenance.format('YYYY-MM-DDTHH:mm:ss') : undefined,
      plannedDateOfStop: data.plannedDateOfStop ? data.plannedDateOfStop.format('YYYY-MM-DDTHH:mm:ss') : undefined,
      userId: data.userId || user.id,
    };

    const result = await createNewEngine(processedData);

    if (result.success) {
      setIsModalOpen(false);
    }
  }, [updateExistingEngine, createNewEngine]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleViewDetails = useCallback((engine: EngineListItem) => {
    if (engine.id) {
      setSelectedEngineId(engine.id);
      setIsDetailModalOpen(true);
    }
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedEngineId(null);
  }, []);

  const handleRequestMaintenance = useCallback((engine: EngineListItem) => {
    if (engine.id) {
      setOrderEngineId(engine.id);
      setIsOrderFormOpen(true);
    }
  }, []);

  const handleOrderFormClose = useCallback(() => {
    setIsOrderFormOpen(false);
    setOrderEngineId(null);
  }, []);

  const handleOrderSubmit = useCallback(async (data: OrderRequestDto) => {
    if (orderEngineId) {
      await createNewOrder({
        data: {
          ...data,
          status: OrderRequestDto.status.NEW,
          engineId: orderEngineId,
        },
        onSuccess: () => {
          loadEngineDetail(orderEngineId);
        },
      });
      setIsOrderFormOpen(false);
      setOrderEngineId(null);
    }
  }, [orderEngineId, createNewOrder, loadEngineDetail]);

  return (
    <div className={styles.enginesPage}>
      <div className={styles.header}>
        <h1>Электрические машины</h1>
      </div>

      <CardGrid>
        {engines.map((engine: EngineListItem) => (
          <EngineCard
            key={engine.id}
            engine={engine}
            onDelete={handleDelete}
            onViewDetails={handleViewDetails}
            onRequestMaintenance={handleRequestMaintenance}
            getStatusInfo={getStatusInfo}
            formatDate={formatDate}
          />
        ))}
        <AddCard onClick={handleCreate} />
      </CardGrid>

      {(isModalOpen) && (
        <EngineForm
          isOpen={isModalOpen}
          isEditing={false}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      )}

      {isDetailModalOpen && (
        <EngineDetailModal
          engineId={selectedEngineId}
          onClose={handleCloseDetailModal}
        />
      )}

      {isOrderFormOpen && (
        <OrderForm
          editingOrder={null}
          isEditing={false}
          onSubmit={handleOrderSubmit}
          onCancel={handleOrderFormClose}
        />
      )}
    </div>
  );
});

EnginesPage.displayName = 'EnginesPage';
